import type { ReactNode } from 'react';
import type { ClientColumn, ClientListRow } from '../types';
import { Card, EmptyState, SectionHead, cn } from '../ui/primitives';

/**
 * The quotes, orders and products lists — one table, three callers.
 *
 * ── Why a table ──
 *
 * These carry the same columns their own overview pages carry, and several of
 * them are dates: a quote row has "client viewed", "deadline" and "created"
 * side by side. Unlabelled, three dates in a row are indistinguishable, and a
 * row missing one leaves a gap the next row fills. Headers are what make a bare
 * value legible and let a column line up down the page.
 *
 * The leading cell stays the row's identity — mark, title, mono sub-line — and
 * everything after it is declared by the caller, so a column added for quotes
 * does not appear on orders.
 *
 * ── The brand tag ──
 *
 * Only products can carry one: `Product` has `brandId`, while `OrderQuote` and
 * `Order` have no brand field at all. Rather than derive a quote's brand from
 * its line items — backend-owned, and ambiguous the moment a quote spans two
 * brands — rows without one render no tag and the column collapses.
 */

function BrandTag({ brand }: { brand: NonNullable<ClientListRow['brand']> }) {
	return (
		<span
			className="flex w-11 shrink-0 items-center gap-1.5 font-mono text-xs text-text-3"
			title={brand.code ?? undefined}
		>
			<span
				className="h-2 w-2 shrink-0 rounded-[2px] ring-1 ring-inset ring-white/25"
				style={{ background: brand.color ?? 'var(--line-2)' }}
			/>
			<span className="truncate">{brand.code}</span>
		</span>
	);
}

export function ClientListSection({
	title,
	meta,
	action,
	columns = [],
	rows,
	icon,
	identityLabel = 'Name',
	emptyTitle,
	emptyHint,
	emptyAction,
	onRowClick,
	rowHref,
	renderRowAction,
}: {
	title: string;
	meta?: ReactNode;
	action?: ReactNode;
	/** The columns after the identity cell. */
	columns?: ClientColumn[];
	rows: ClientListRow[];
	/** The glyph in each row's mark — the app passes its own icon element. */
	icon?: ReactNode;
	/** Header over the identity column — "Quote", "Order", "Product". */
	identityLabel?: string;
	emptyTitle: string;
	emptyHint?: string;
	emptyAction?: ReactNode;
	/**
	 * Both navigation slots are the app's. `rowHref` renders a real anchor on
	 * the title, so middle-click and "open in new tab" work; `onRowClick` makes
	 * the whole row clickable. Passing neither leaves the list read-only, which
	 * is legitimate for a viewer without permission to follow it.
	 */
	onRowClick?: (row: ClientListRow) => void;
	rowHref?: (row: ClientListRow) => string | undefined;
	renderRowAction?: (row: ClientListRow) => ReactNode;
}) {
	const anyBrand = rows.some((r) => r.brand);

	if (rows.length === 0) {
		return (
			<>
				<SectionHead title={title} meta={meta} action={action} />
				<EmptyState title={emptyTitle} hint={emptyHint} action={emptyAction} />
			</>
		);
	}

	return (
		<>
			<SectionHead title={title} meta={meta} action={action} />
			<Card>
				{/* Scrolls inside its own card rather than pushing the page
				    sideways on a narrow viewport. */}
				<div className="overflow-x-auto">
					<table className="w-full border-collapse text-left">
						<thead>
							<tr className="border-b border-line-1">
								<th className="px-4.5 py-2.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									{identityLabel}
								</th>
								{columns.map((column) => (
									<th
										key={column.key}
										className={cn(
											'whitespace-nowrap px-3 py-2.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4',
											column.align === 'right' && 'text-right',
											column.collapse && 'hidden xl:table-cell'
										)}
									>
										{column.label}
									</th>
								))}
								{renderRowAction && <th className="w-10 px-3 py-2.5" />}
							</tr>
						</thead>
						<tbody>
							{rows.map((row, i) => {
								const href = rowHref?.(row);
								const interactive = Boolean(href || onRowClick);

								return (
									<tr
										key={row.id}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										className={cn(
											i < rows.length - 1 && 'border-b border-line-1',
											interactive &&
												'cursor-pointer transition-colors duration-[var(--dur-fast)] hover:bg-surface-hover'
										)}
									>
										<td className="px-4.5 py-3">
											<div className="flex items-center gap-3">
												{anyBrand &&
													(row.brand ? (
														<BrandTag brand={row.brand} />
													) : (
														/* Holds the column so titles stay
														   aligned when only some rows carry
														   a brand. */
														<span className="w-11 shrink-0" />
													))}

												<span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded-[var(--r-2)] border border-line-2 bg-surface-fill text-text-4">
													{row.imageUrl ? (
														<img
															src={row.imageUrl}
															alt=""
															className="h-full w-full object-cover"
														/>
													) : (
														icon
													)}
												</span>

												<span className="min-w-0">
													{href ? (
														<a
															href={href}
															className="block truncate text-base font-medium text-text-1 no-underline hover:underline"
														>
															{row.title}
														</a>
													) : (
														<span className="block truncate text-base font-medium text-text-1">
															{row.title}
														</span>
													)}
													{row.subtitle && (
														<span className="mt-0.5 block truncate font-mono text-xs text-text-4">
															{row.subtitle}
														</span>
													)}
												</span>
											</div>
										</td>

										{columns.map((column) => (
											<td
												key={column.key}
												className={cn(
													'whitespace-nowrap px-3 py-3 text-base text-text-3',
													column.align === 'right' && 'text-right',
													column.collapse && 'hidden xl:table-cell'
												)}
											>
												{row.cells?.[column.key] ?? (
													<span className="text-text-4">—</span>
												)}
											</td>
										))}

										{renderRowAction && (
											<td className="px-3 py-3">
												<div className="flex justify-end">
													{renderRowAction(row)}
												</div>
											</td>
										)}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>
		</>
	);
}
